import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Page } from '../../components';
import { ReactComponent as StepComplete } from '../../assets/images/step-complete.svg';
import { ReactComponent as RadioChecked } from '../../assets/images/radio-button-checked.svg';
import { ReactComponent as RadioUnchecked } from '../../assets/images/radio-button-unchecked.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/info-stroke.svg';
import useMappingToolStore, { HEADER_VALUES } from './store';
import { fitScaleFor } from './pdfScale';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`;

const STEPS = [
  { key: 'upload', label: 'Upload file' },
  { key: 'mapping', label: 'Mapping' },
  { key: 'review', label: 'Review' },
  { key: 'testing', label: 'Testing' }
];

const CURRENT_STEP_INDEX = 2;
const MAPPING_PATH = '/report_template_mapper/tool';
const TESTING_PATH = '/report_template_mapper/testing';

// passMark/failMark are the chosen option's `id` (see store.js's fetchPromptSettings) --
// look up that option's literal `mark` to get what should actually be written on the form.
const resolveMark = (id, options) => options.find(option => option.value === id)?.mark ?? '';

// Marks Helvetica's WinAnsi encoding cannot represent -- these only render through
// ZapfDingbats, whose own encoding maps them to the matching glyph. Every other mark the
// backend offers (X, P, F, ...) is plain ASCII and draws with Helvetica as normal.
const DINGBAT_MARKS = new Set(['\u2713', '\u2717', '\u2718']);

// The leading dingbat glyph of a value, or '' when it starts with something Helvetica can
// encode. A value is a single mark or a header example, so only its first char can be one.
const dingbatPrefix = text => (DINGBAT_MARKS.has(text.charAt(0)) ? text.charAt(0) : '');

// The example value for a mapped field: a prompt's configured outcome mark, or a header/calendar
// chip's mock example, falling back to its label (for calendar chips the label IS the value).
const resolveFieldValue = (
  mappedKey,
  headerValues,
  passMark,
  failMark,
  naMark,
  passMarkOptions,
  failMarkOptions,
  naMarkOptions
) => {
  if (!mappedKey) return '';
  if (mappedKey.promptId) {
    if (mappedKey.outcome === 'fail') return resolveMark(failMark, failMarkOptions);
    if (mappedKey.outcome === 'pass') return resolveMark(passMark, passMarkOptions);
    if (mappedKey.outcome === 'na') return resolveMark(naMark, naMarkOptions);
    return resolveMark(passMark, passMarkOptions);
  }
  return headerValues[mappedKey.key] ?? mappedKey.label;
};

const ReviewPdf = () => {
  const history = useHistory();
  const [
    {
      file,
      formName,
      formState,
      formYear,
      fields: mappedFields,
      passMark,
      failMark,
      naMark,
      passMarkOptions,
      failMarkOptions,
      naMarkOptions
    }
  ] = useMappingToolStore();
  const headerValues = HEADER_VALUES;

  const canvasRef = useRef(null);
  const pdfCardRef = useRef(null);

  const [filledDoc, setFilledDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [status, setStatus] = useState('');
  const [loadError, setLoadError] = useState('');

  const hasMappedFields = mappedFields.some(f => f.mappedKey);

  const goToMapping = () => history.push(MAPPING_PATH);
  const goToTesting = () => history.push(TESTING_PATH);

  // Fills a fresh copy of the PDF with each mapped field's resolved example value -- both
  // as the real AcroForm field value (for viewers that render field appearances) and as
  // text drawn directly onto the page content (since a plain pdf.js render, used for the
  // preview below, skips annotation appearance streams entirely) -- then re-renders the
  // result with pdf.js so the preview shows exactly what got saved.
  const generateFilledPreview = useCallback(async () => {
    if (!file) return;
    setLoadError('');
    setStatus('Generating…');
    try {
      const buffer = await file.arrayBuffer();
      const doc = await PDFDocument.load(buffer.slice(0));
      const pages = doc.getPages();
      let form = null;
      try {
        form = doc.getForm();
      } catch {
        // some real PDFs have no AcroForm at all; the drawn overlay text below still works
      }

      // Standard 14 fonts, built into every compliant PDF viewer -- no font file/fontkit
      // needed. Helvetica is what every value gets measured and drawn in; ZapfDingbats'
      // own encoding maps plain ASCII to a check-mark GLYPH, the only way to draw an actual
      // checkmark at all (Helvetica can't encode it).
      const baseFont = await doc.embedFont(StandardFonts.Helvetica);
      // Any of the three configured marks can be a dingbat -- Pass = ✓ paired with Fail = ✗
      // is the common setup -- so the font is loaded off all of them, not just Pass. This
      // also guarantees dingbatsFont exists for every value dingbatPrefix() matches below.
      const needsDingbats = [
        resolveMark(passMark, passMarkOptions),
        resolveMark(failMark, failMarkOptions),
        resolveMark(naMark, naMarkOptions)
      ].some(mark => dingbatPrefix(mark));
      const dingbatsFont = needsDingbats ? await doc.embedFont(StandardFonts.ZapfDingbats) : null;

      // The dropped box's width is the one real constraint on how this value gets drawn --
      // rather than let pdf-lib's own maxWidth wrap long values onto a second line (which
      // breaks a form field meant to print on one line), shrink the font until the whole
      // value fits the box at its own width, down to a floor past which it'd be illegible.
      const MIN_FONT_SIZE = 4;
      const widthOfValue = (text, size) => {
        const glyph = dingbatsFont ? dingbatPrefix(text) : '';
        if (glyph) {
          const rest = text.slice(glyph.length);
          return (
            dingbatsFont.widthOfTextAtSize(glyph, size) +
            (rest ? baseFont.widthOfTextAtSize(rest, size) : 0)
          );
        }
        return baseFont.widthOfTextAtSize(text, size);
      };
      const fitFontSize = (text, startSize, availableWidth) => {
        const width = widthOfValue(text, startSize);
        if (width <= availableWidth || startSize <= MIN_FONT_SIZE) return startSize;
        return Math.max(MIN_FONT_SIZE, startSize * (availableWidth / width));
      };

      // ZapfDingbats' mark glyphs render visibly smaller than their nominal point size
      // suggests, next to regular Helvetica text at the same size -- boosted only for the
      // actual draw call (not the width measurement/fit-to-box math above, so the box-fit
      // and centering logic stays based on the glyph's real advance width).
      const DINGBAT_DRAW_SIZE_BOOST = 1.6;

      const drawMark = (page, text, opts) => {
        const glyph = dingbatsFont ? dingbatPrefix(text) : '';
        if (!glyph) {
          page.drawText(text, opts);
          return;
        }
        page.drawText(glyph, {
          ...opts,
          font: dingbatsFont,
          size: opts.size * DINGBAT_DRAW_SIZE_BOOST
        });
        const rest = text.slice(glyph.length);
        if (rest) {
          const glyphWidth = dingbatsFont.widthOfTextAtSize(glyph, opts.size);
          page.drawText(rest, { ...opts, x: opts.x + glyphWidth });
        }
      };

      let filledCount = 0;
      mappedFields
        .filter(f => f.mappedKey)
        .forEach(f => {
          const value = resolveFieldValue(
            f.mappedKey,
            headerValues,
            passMark,
            failMark,
            naMark,
            passMarkOptions,
            failMarkOptions,
            naMarkOptions
          );
          if (!value) return;
          const page = pages[f.page - 1];
          if (!page) return;
          filledCount += 1;

          // Stored x/y/w/h are viewport pixels (top-left origin) at whatever scale the
          // field was captured at on the Mapping step -- convert back to PDF points
          // (bottom-left origin) using that field's own scale.
          const scale = f.scale || 1;
          const pageHeightPt = page.getHeight();
          const rectXPt = f.x / scale;
          const rectWPt = f.w / scale;
          const rectHPt = f.h / scale;
          const rectYPt = pageHeightPt - (f.y + f.h) / scale;
          const availableWidthPt = Math.max(4, rectWPt - 4);
          const fontSize = fitFontSize(
            value,
            Math.max(6, Math.min(9, rectHPt * 0.6)),
            availableWidthPt
          );
          // Centered both ways in the dropped box -- horizontally against the value's own
          // measured width (so a short value like a state abbreviation doesn't just sit
          // pinned to the box's left edge), vertically against the font size the same way
          // as before.
          const valueWidth = widthOfValue(value, fontSize);
          const opts = {
            x: rectXPt + Math.max(2, (rectWPt - valueWidth) / 2),
            y: rectYPt + Math.max(1, (rectHPt - fontSize) / 2),
            size: fontSize,
            color: rgb(0, 0, 0),
            font: baseFont
          };

          if (f.source === 'acro' && f.name && form) {
            try {
              const field = form.getField(f.name);
              if (field.constructor.name === 'PDFCheckBox') {
                if (value) field.check();
              } else if (typeof field.setText === 'function' && !dingbatPrefix(value)) {
                field.setText(value);
              }
            } catch {
              // pdf-lib can't set every real field directly; the drawn overlay below still
              // shows the value in the preview render
            }
          }

          drawMark(page, value, opts);
        });

      const outBytes = await doc.save();
      const previewDoc = await pdfjsLib.getDocument({ data: outBytes.slice(0) }).promise;
      setFilledDoc(previewDoc);
      setNumPages(previewDoc.numPages);
      setPageNum(1);
      setStatus(
        filledCount
          ? `Showing ${filledCount} mapped field${filledCount === 1 ? '' : 's'} with example data.`
          : 'No mapped fields have example data to show yet.'
      );
    } catch {
      setLoadError('Could not generate the filled preview.');
    }
  }, [
    file,
    mappedFields,
    headerValues,
    passMark,
    failMark,
    naMark,
    passMarkOptions,
    failMarkOptions,
    naMarkOptions
  ]);

  useEffect(() => {
    generateFilledPreview();
    // Only re-run when a different file/mapping set arrives, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  useEffect(() => {
    if (!filledDoc) return undefined;
    let cancelled = false;
    filledDoc.getPage(pageNum).then(async page => {
      if (cancelled) return;
      const naturalViewport = page.getViewport({ scale: 1 });
      const fitScale = fitScaleFor(naturalViewport.width, pdfCardRef.current);
      const viewport = page.getViewport({ scale: fitScale });
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');
        if (context) {
          try {
            await page.render({ canvasContext: context, viewport }).promise;
          } catch {
            // best-effort render; environments without a real 2d canvas context land here
          }
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filledDoc, pageNum]);

  const goToPrevPage = () => setPageNum(n => Math.max(1, n - 1));
  const goToNextPage = () => setPageNum(n => Math.min(numPages, n + 1));

  const pdfTitle = [formState, formName, formYear].filter(Boolean).join(' | ') || 'Untitled form';
  const pageInfo = `Page ${pageNum} of ${numPages || 1}`;

  return (
    <Page
      documentName="Inspection Report Template Mapper"
      pageName="Inspection Report Template Mapper - Review"
      path="/report_template_mapper/review"
    >
      <div className="mapping-tool-page">
        <div className="mapping-tool-header d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mapping-tool-title">Inspection Report Template Mapper - Review</h1>
            <p className="mapping-tool-subtitle">Review your mapped fields</p>
          </div>
          <div className="upload-form-header-actions">
            <button type="button" className="btn upload-form-cancel-btn" onClick={goToMapping}>
              Return
            </button>
            <button
              type="button"
              className="btn btn-primary mapping-tool-new-btn"
              disabled={!file || !hasMappedFields}
              onClick={goToTesting}
            >
              Test Mapped Form
            </button>
          </div>
        </div>

        <div className="mapping-tool-list-card">
          <div className="upload-form-stepper">
            {STEPS.map((step, index) => (
              <Fragment key={step.key}>
                <div className="upload-form-step">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {index < CURRENT_STEP_INDEX ? (
                    <StepComplete className="upload-form-step-icon" />
                  ) : index === CURRENT_STEP_INDEX ? (
                    <RadioChecked className="upload-form-step-icon" />
                  ) : (
                    <RadioUnchecked className="upload-form-step-icon" />
                  )}
                  <span className="upload-form-step-label">{step.label}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <span
                    className={`upload-form-step-line ${
                      index < CURRENT_STEP_INDEX ? 'active' : ''
                    }`}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>

        <div className="upload-form-info-banner">
          <InfoIcon className="upload-form-info-icon" />
          <p className="upload-form-info-text mapping-tool-info-single-line">
            {status || 'Generating the filled preview…'}
          </p>
        </div>

        <div className="mapping-tool-pdf-card" ref={pdfCardRef}>
          <p className="mapping-tool-pdf-title">{pdfTitle}</p>
          <div className="mapping-tool-pdf-toolbar">
            <button
              type="button"
              className="btn upload-form-cancel-btn"
              disabled={pageNum <= 1}
              onClick={goToPrevPage}
            >
              Prev page
            </button>
            <span className="mapping-tool-pdf-page-info">{pageInfo}</span>
            <button
              type="button"
              className="btn upload-form-cancel-btn"
              disabled={pageNum >= numPages}
              onClick={goToNextPage}
            >
              Next page
            </button>
          </div>

          {!file && (
            <div className="mapping-tool-pdf-stage mapping-tool-pdf-empty">
              <p className="mapping-tool-pdf-placeholder">
                No PDF loaded — go back to Mapping to select one.
              </p>
            </div>
          )}

          {file && loadError && (
            <div className="mapping-tool-pdf-stage mapping-tool-pdf-empty">
              <p className="mapping-tool-pdf-placeholder">{loadError}</p>
            </div>
          )}

          {file && !loadError && (
            <div className="mapping-tool-stage">
              <canvas ref={canvasRef} className="mapping-tool-pdf-canvas" />
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default ReviewPdf;
